import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useOne } from '@refinedev/core';
import { EditableLeafletMap, SelectedFeature, MapMode, EditableLeafletMapRef } from '../../components/map';
import { IMDFParser, IMDFFileTypes, getIMDFDisplayName } from '../../utils/imdf-parser';

interface IMDFFile {
  id: string;
  originalName: string;
  storagePath: string;
  description?: string;
  version?: string;
  category?: string;
  uploadedBy?: string;
  fileSize: number;
  status: 'pending' | 'validating' | 'valid' | 'invalid';
  createdAt: string;
  updatedAt: string;
}

interface FloorLevel {
  id: string;
  name: string;
  ordinal: number;
  shortName?: string;
}

// IMDF 特徵類型選項
const IMDF_FEATURE_TYPES = [
  { value: 'unit', label: '單位 (Unit)', description: '房間、商店等空間單位' },
  { value: 'amenity', label: '設施 (Amenity)', description: '洗手間、ATM、電梯等' },
  { value: 'anchor', label: '錨點 (Anchor)', description: '導航參考點' },
  { value: 'opening', label: '開口 (Opening)', description: '門、窗等開口' },
  { value: 'fixture', label: '固定裝置 (Fixture)', description: '固定設備' },
  { value: 'kiosk', label: '亭子 (Kiosk)', description: '資訊亭、售票亭等' },
  { value: 'occupant', label: '佔用者 (Occupant)', description: '商戶、租戶等' },
  { value: 'detail', label: '細節 (Detail)', description: '其他細節元素' },
  { value: 'section', label: '區域 (Section)', description: '樓層區域' },
  { value: 'geofence', label: '地理圍欄 (Geofence)', description: '虛擬邊界' },
];

// 生成 UUID
const generateUUID = (): string => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

const IMDFEditorWithFloors: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const mapRef = useRef<EditableLeafletMapRef>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [originalGeoJsonData, setOriginalGeoJsonData] = useState<GeoJSON.FeatureCollection | null>(null);
  const [filteredGeoJsonData, setFilteredGeoJsonData] = useState<GeoJSON.FeatureCollection | null>(null);
  const [imdfParser, setImdfParser] = useState<IMDFParser | null>(null);
  const [imdfData, setImdfData] = useState<IMDFFileTypes>({});
  const [availableLayers, setAvailableLayers] = useState<string[]>([]);
  const [selectedLayers, setSelectedLayers] = useState<string[]>([]);
  const [floorLevels, setFloorLevels] = useState<FloorLevel[]>([]);
  const [currentFloor, setCurrentFloor] = useState<string | null>(null);
  const [selectedFeature, setSelectedFeature] = useState<SelectedFeature | null>(null);
  const [editingProperty, setEditingProperty] = useState<string | null>(null);
  const [mapMode, setMapMode] = useState<MapMode>('view');
  const [selectedFeatureType, setSelectedFeatureType] = useState<string>('unit');
  const [pendingFeature, setPendingFeature] = useState<GeoJSON.Feature | null>(null);

  // 獲取 IMDF 檔案資訊
  const { data: imdfFile, isLoading: isLoadingFile, error: fileError } = useOne<IMDFFile>({
    resource: "imdf",
    id: id!,
    queryOptions: {
      enabled: !!id,
    },
  });

  // 載入 IMDF 檔案的 GeoJSON 資料
  useEffect(() => {
    const loadIMDFData = async () => {
      if (!imdfFile?.data) return;

      try {
        setLoading(true);
        setError(null);

        const API_URL = import.meta.env.VITE_API_URL || 'http://imdf.sto-tek.com';
        const token = localStorage.getItem('token');

        console.log('載入 IMDF 檔案:', imdfFile.data.originalName);

        const response = await fetch(`${API_URL}/imdf/files/${id}/download`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const blob = await response.blob();
        console.log('下載完成，檔案大小:', blob.size);
        
        // 使用 IMDF 解析器處理檔案
        const parser = new IMDFParser();
        console.log('開始解析 ZIP 檔案...');
        
        await parser.loadZipFile(blob);
        console.log('ZIP 檔案載入完成');
        
        const parsedData = await parser.parseAllFiles();
        console.log('IMDF 解析完成:', Object.keys(parsedData));
        
        setImdfParser(parser);
        setImdfData(parsedData);
        
        // 獲取合併的 GeoJSON 資料
        const combinedData = parser.getCombinedGeoJSON();
        console.log('合併 GeoJSON 完成，特徵數量:', combinedData.features.length);
        setOriginalGeoJsonData(combinedData);
        setFilteredGeoJsonData(combinedData);
        
        // 提取可用的圖層
        const layers = parser.getAvailableLayers();
        console.log('可用圖層:', layers);
        setAvailableLayers(layers);
        
        // 預設選擇所有圖層
        setSelectedLayers(layers);
        
        // 獲取樓層資訊
        const floors = parser.getFloorLevels();
        console.log('樓層資訊:', floors);
        setFloorLevels(floors);
        
        // 如果有樓層，設定預設為查看所有樓層
        if (floors.length > 0) {
          setCurrentFloor(null); // null 表示顯示所有樓層
        }
        
      } catch (err) {
        console.error('載入 IMDF 檔案時發生錯誤:', err);
        setError(err instanceof Error ? err.message : '載入失敗');
      } finally {
        setLoading(false);
      }
    };

    if (!isLoadingFile && imdfFile?.data) {
      loadIMDFData();
    }
  }, [imdfFile, id, isLoadingFile]);

  // 當樓層或原始資料變化時，更新過濾的 GeoJSON 資料
  useEffect(() => {
    if (!originalGeoJsonData || !imdfParser) return;

    let filteredData: GeoJSON.FeatureCollection;

    if (currentFloor === null) {
      // 顯示所有樓層
      filteredData = originalGeoJsonData;
    } else {
      // 過濾特定樓層的特徵
      filteredData = imdfParser.getFeaturesByLevel(currentFloor);
    }

    setFilteredGeoJsonData(filteredData);
    console.log('樓層過濾完成:', currentFloor, '特徵數量:', filteredData.features.length);
  }, [currentFloor, originalGeoJsonData, imdfParser]);

  // 處理樓層切換
  const handleFloorChange = (floorId: string | null) => {
    setCurrentFloor(floorId);
    setSelectedFeature(null); // 切換樓層時清除選取
    setPendingFeature(null);
    console.log('切換樓層:', floorId);
  };

  // 處理圖層切換
  const handleLayerToggle = (layerType: string) => {
    setSelectedLayers(prev => {
      const newSelectedLayers = prev.includes(layerType)
        ? prev.filter(l => l !== layerType)
        : [...prev, layerType];
      
      console.log('圖層切換:', layerType, newSelectedLayers);
      return newSelectedLayers;
    });
  };

  // 處理全選/全不選
  const handleSelectAllLayers = () => {
    if (selectedLayers.length === availableLayers.length) {
      // 全部選中，執行全不選
      setSelectedLayers([]);
      console.log('全不選圖層');
    } else {
      // 部分選中或全不選，執行全選
      setSelectedLayers(availableLayers);
      console.log('全選圖層');
    }
  };

  // 獲取圖層狀態文字
  const getLayerStatusText = () => {
    if (selectedLayers.length === 0) return '全部隱藏';
    if (selectedLayers.length === availableLayers.length) return '全部顯示';
    return `${selectedLayers.length}/${availableLayers.length} 顯示`;
  };

  // 獲取當前樓層的特徵數量
  const getCurrentFloorFeatureCount = () => {
    if (!filteredGeoJsonData) return 0;
    return filteredGeoJsonData.features.length;
  };

  // 獲取樓層狀態文字
  const getFloorStatusText = () => {
    if (currentFloor === null) return '全部樓層';
    const floor = floorLevels.find(f => f.id === currentFloor);
    return floor ? floor.name : '未知樓層';
  };

  // 處理特徵選取
  const handleFeatureSelect = (feature: SelectedFeature | null) => {
    setSelectedFeature(feature);
    setEditingProperty(null);
    setPendingFeature(null);
    console.log('選取特徵:', feature?.feature.properties);
  };

  // 處理屬性編輯
  const handlePropertyEdit = (property: string, value: any) => {
    if (!selectedFeature?.feature && !pendingFeature) return;

    const targetFeature = selectedFeature?.feature || pendingFeature;
    if (!targetFeature) return;

    const updatedFeature = {
      ...targetFeature,
      properties: {
        ...targetFeature.properties,
        [property]: value,
      },
    };

    // 如果是待處理特徵，更新待處理特徵
    if (pendingFeature) {
      setPendingFeature(updatedFeature);
      return;
    }

    // 如果是已選取特徵，更新選取的特徵
    if (selectedFeature) {
      const updatedSelectedFeature = {
        ...selectedFeature,
        feature: updatedFeature,
      };
      setSelectedFeature(updatedSelectedFeature);

      // 更新原始 GeoJSON 資料
      if (originalGeoJsonData) {
        const updatedFeatures = originalGeoJsonData.features.map(f =>
          f.properties?.id === updatedFeature.properties?.id ? updatedFeature : f
        );
        
        const updatedGeoJsonData = {
          ...originalGeoJsonData,
          features: updatedFeatures,
        };
        
        setOriginalGeoJsonData(updatedGeoJsonData);
        console.log('屬性已更新:', property, '=', value);
      }
    }
  };

  // 處理地圖模式切換
  const handleMapModeChange = (mode: MapMode) => {
    setMapMode(mode);
    setSelectedFeature(null);
    setPendingFeature(null);
    console.log('地圖模式切換到:', mode);
  };

  // 處理特徵創建
  const handleFeatureCreate = (feature: GeoJSON.Feature, layerType: string) => {
    console.log('創建新特徵:', feature, '類型:', selectedFeatureType);
    
    // 生成新的 UUID 和基本屬性
    const uuid = generateUUID();
    const newFeature = {
      ...feature,
      properties: {
        id: uuid,
        name: `新的${IMDF_FEATURE_TYPES.find(t => t.value === selectedFeatureType)?.label || selectedFeatureType}`,
        _file_type: selectedFeatureType,
        _source_file: `${selectedFeatureType}.geojson`,
        created_at: new Date().toISOString(),
        category: selectedFeatureType,
        // 如果當前選擇了特定樓層，設定 level_id
        ...(currentFloor && { level_id: currentFloor }),
      },
    };

    // 設置為待處理特徵，讓用戶編輯屬性
    setPendingFeature(newFeature);
    setSelectedFeature(null);
    
    console.log('新特徵已創建，等待用戶編輯:', uuid);
  };

  // 確認添加特徵
  const handleConfirmFeature = () => {
    if (!pendingFeature || !originalGeoJsonData) return;

    const updatedGeoJsonData = {
      ...originalGeoJsonData,
      features: [...originalGeoJsonData.features, pendingFeature],
    };
    
    setOriginalGeoJsonData(updatedGeoJsonData);
    setPendingFeature(null);
    
    // 確保新特徵的圖層是可見的
    const featureType = pendingFeature.properties?._file_type;
    if (featureType && !selectedLayers.includes(featureType)) {
      setSelectedLayers(prev => [...prev, featureType]);
    }
    
    console.log('新特徵已添加到資料中:', pendingFeature.properties?.id);
  };

  // 取消添加特徵
  const handleCancelFeature = () => {
    setPendingFeature(null);
    console.log('取消添加特徵');
  };

  // 處理特徵編輯
  const handleFeatureEdit = (feature: GeoJSON.Feature, layerType: string) => {
    console.log('編輯特徵幾何:', feature, '類型:', layerType);
    
    if (originalGeoJsonData) {
      const updatedFeatures = originalGeoJsonData.features.map(f =>
        f.properties?.id === feature.properties?.id ? feature : f
      );
      
      const updatedGeoJsonData = {
        ...originalGeoJsonData,
        features: updatedFeatures,
      };
      
      setOriginalGeoJsonData(updatedGeoJsonData);
      console.log('特徵幾何已更新');
    }
  };

  // 處理特徵刪除
  const handleFeatureDelete = (feature: GeoJSON.Feature, layerType: string) => {
    console.log('刪除特徵:', feature, '類型:', layerType);
    
    if (originalGeoJsonData) {
      const updatedFeatures = originalGeoJsonData.features.filter(f =>
        f.properties?.id !== feature.properties?.id
      );
      
      const updatedGeoJsonData = {
        ...originalGeoJsonData,
        features: updatedFeatures,
      };
      
      setOriginalGeoJsonData(updatedGeoJsonData);
      setSelectedFeature(null);
      console.log('特徵已從資料中刪除');
    }
  };

  // 新增屬性
  const handleAddProperty = () => {
    const newProperty = prompt('輸入新屬性名稱:');
    if (newProperty && !newProperty.startsWith('_')) {
      handlePropertyEdit(newProperty, '');
    }
  };

  // 刪除特徵
  const handleDeleteFeature = () => {
    if (!selectedFeature?.feature) return;
    
    const confirmed = confirm('確定要刪除此特徵嗎？');
    if (confirmed) {
      handleFeatureDelete(selectedFeature.feature, selectedFeature.layerType);
    }
  };

  // 定位到特徵
  const handleFocusFeature = () => {
    if (selectedFeature?.feature && mapRef.current) {
      mapRef.current.focusOnFeature(selectedFeature.feature);
    }
  };

  // 載入狀態
  if (loading || isLoadingFile) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600">
          {isLoadingFile ? '載入檔案資訊...' : '解析 IMDF 檔案...'}
        </span>
      </div>
    );
  }

  // 錯誤狀態
  if (fileError || error) {
    return (
      <div className="p-8 text-center text-red-500">
        <h2 className="text-xl font-bold mb-4">載入失敗</h2>
        <p className="mb-4">{fileError?.message || error || '未知錯誤'}</p>
        <button 
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          重新載入
        </button>
      </div>
    );
  }

  // 決定要顯示的特徵（待處理特徵優先）
  const displayFeature = pendingFeature || selectedFeature?.feature;
  const isEditing = Boolean(pendingFeature);

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      {/* 標題列 */}
      <div className="bg-white shadow-sm border-b px-6 py-4">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-xl font-bold text-gray-900">IMDF 編輯器</h1>
            <p className="text-sm text-gray-600">
              {imdfFile?.data?.originalName || '未知檔案'} - {imdfFile?.data?.status || '未知狀態'}
            </p>
          </div>
          <div className="flex gap-3">
            {/* 地圖模式切換 */}
            <div className="flex bg-gray-100 rounded-md p-1">
              {(['view', 'edit', 'draw'] as MapMode[]).map(mode => (
                <button
                  key={mode}
                  onClick={() => handleMapModeChange(mode)}
                  className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                    mapMode === mode
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {mode === 'view' ? '檢視' : mode === 'edit' ? '編輯' : '繪製'}
                </button>
              ))}
            </div>
            
            <button 
              onClick={() => {
                if (imdfParser) {
                  const validation = imdfParser.validateIMDFStructure();
                  alert(`驗證結果:\n有效: ${validation.isValid}\n錯誤: ${validation.errors.length}\n警告: ${validation.warnings.length}`);
                }
              }}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
            >
              驗證
            </button>
            <button 
              onClick={() => {
                if (imdfParser) {
                  imdfParser.exportToZip().then(blob => {
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `${imdfFile?.data?.originalName || 'imdf-export'}.zip`;
                    document.body.appendChild(a);
                    a.click();
                    URL.revokeObjectURL(url);
                    document.body.removeChild(a);
                  });
                }
              }}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              匯出
            </button>
          </div>
        </div>
      </div>

      {/* 主要內容區域 */}
      <div className="flex-1 flex">
        {/* 左側面板 */}
        <div className="w-80 bg-white shadow-sm border-r flex flex-col">
          <div className="p-4">
            <h2 className="font-semibold text-gray-900">檔案資訊</h2>
            <div className="mt-4 space-y-2">
              <div>
                <span className="text-sm text-gray-600">檔案名稱：</span>
                <span className="text-sm font-medium">{imdfFile?.data?.originalName || 'N/A'}</span>
              </div>
              <div>
                <span className="text-sm text-gray-600">狀態：</span>
                <span className="text-sm font-medium">{imdfFile?.data?.status || 'N/A'}</span>
              </div>
              <div>
                <span className="text-sm text-gray-600">大小：</span>
                <span className="text-sm font-medium">
                  {imdfFile?.data?.fileSize ? `${(imdfFile.data.fileSize / 1024 / 1024).toFixed(2)} MB` : 'N/A'}
                </span>
              </div>
              <div>
                <span className="text-sm text-gray-600">模式：</span>
                <span className={`text-sm font-medium ${
                  mapMode === 'view' ? 'text-blue-600' : 
                  mapMode === 'edit' ? 'text-orange-600' : 'text-green-600'
                }`}>
                  {mapMode === 'view' ? '檢視模式' : mapMode === 'edit' ? '編輯模式' : '繪製模式'}
                </span>
              </div>
              <div>
                <span className="text-sm text-gray-600">樓層：</span>
                <span className="text-sm font-medium text-purple-600">
                  {getFloorStatusText()}
                </span>
              </div>
            </div>
          </div>
          
          {/* 樓層切換 */}
          {floorLevels.length > 0 && (
            <div className="border-t">
              <div className="p-4">
                <h3 className="font-semibold text-gray-900 mb-3">樓層導航</h3>
                <div className="space-y-2">
                  <button
                    onClick={() => handleFloorChange(null)}
                    className={`w-full px-3 py-2 text-left rounded-md transition-colors ${
                      currentFloor === null
                        ? 'bg-purple-50 text-purple-700 border border-purple-200'
                        : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <div className="flex justify-between items-center">
                      <span>全部樓層</span>
                      <span className="text-xs">
                        {originalGeoJsonData?.features.length || 0}
                      </span>
                    </div>
                  </button>
                  {floorLevels.map(floor => (
                    <button
                      key={floor.id}
                      onClick={() => handleFloorChange(floor.id)}
                      className={`w-full px-3 py-2 text-left rounded-md transition-colors ${
                        currentFloor === floor.id
                          ? 'bg-purple-50 text-purple-700 border border-purple-200'
                          : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      <div className="flex justify-between items-center">
                        <div>
                          <div className="font-medium">{floor.name}</div>
                          {floor.shortName && (
                            <div className="text-xs text-gray-500">{floor.shortName}</div>
                          )}
                        </div>
                        <span className="text-xs">
                          {currentFloor === floor.id ? getCurrentFloorFeatureCount() : 
                           originalGeoJsonData?.features.filter(f => 
                             f.properties?.level_id === floor.id || 
                             f.properties?.id === floor.id ||
                             (f.properties?._file_type === 'level' && f.properties?.id === floor.id)
                           ).length || 0}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
          
          {/* 特徵類型選擇（僅在繪製模式顯示） */}
          {mapMode === 'draw' && (
            <div className="border-t">
              <div className="p-4">
                <h3 className="font-semibold text-gray-900 mb-3">選擇特徵類型</h3>
                <select
                  value={selectedFeatureType}
                  onChange={(e) => setSelectedFeatureType(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {IMDF_FEATURE_TYPES.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
                <p className="mt-2 text-xs text-gray-500">
                  {IMDF_FEATURE_TYPES.find(t => t.value === selectedFeatureType)?.description}
                </p>
              </div>
            </div>
          )}
          
          {/* 圖層管理 */}
          <div className="border-t flex-1 flex flex-col">
            <div className="p-4 pb-2">
              <div className="flex justify-between items-center mb-3">
                <h3 className="font-semibold text-gray-900">圖層管理</h3>
                <button
                  onClick={handleSelectAllLayers}
                  className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                >
                  {selectedLayers.length === availableLayers.length ? '全不選' : '全選'}
                </button>
              </div>
              <div className="text-xs text-gray-500 mb-3">
                {getLayerStatusText()}
              </div>
            </div>
            
            <div className="flex-1 overflow-y-auto">
              {availableLayers.length > 0 ? (
                availableLayers.map(layer => {
                  const isSelected = selectedLayers.includes(layer);
                  const featureCount = filteredGeoJsonData?.features.filter(f => f.properties?._file_type === layer).length || 0;
                  
                  return (
                    <div
                      key={layer}
                      className={`p-3 border-b hover:bg-gray-50 transition-colors ${
                        isSelected ? 'bg-blue-50' : ''
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <label className="flex items-center cursor-pointer flex-1">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handleLayerToggle(layer)}
                            className="mr-3 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <div className="flex-1">
                            <span className={`text-sm font-medium ${
                              isSelected ? 'text-blue-900' : 'text-gray-900'
                            }`}>
                              {layer}
                            </span>
                            <div className="text-xs text-gray-500 mt-1">
                              {featureCount} 個特徵
                            </div>
                          </div>
                        </label>
                        <div className={`w-3 h-3 rounded-full ${
                          isSelected ? 'bg-blue-500' : 'bg-gray-300'
                        }`} />
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="p-4 text-center text-gray-500">
                  沒有可用的圖層
                </div>
              )}
            </div>
          </div>
        </div>

        {/* 中間區域 - 地圖 */}
        <div className="flex-1 relative">
          {filteredGeoJsonData && filteredGeoJsonData.features.length > 0 ? (
            <EditableLeafletMap
              ref={mapRef}
              geoJsonData={filteredGeoJsonData}
              height="100%"
              className="w-full h-full"
              mode={mapMode}
              selectedLayers={selectedLayers}
              onFeatureSelect={handleFeatureSelect}
              onFeatureEdit={handleFeatureEdit}
              onFeatureCreate={handleFeatureCreate}
              onFeatureDelete={handleFeatureDelete}
              enableClustering={false}
              fitBounds={true}
            />
          ) : (
            <div className="flex items-center justify-center h-full bg-gray-100">
              <div className="text-center">
                <div className="text-4xl text-gray-400 mb-4">🏢</div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  {filteredGeoJsonData ? `${getFloorStatusText()}沒有資料` : '載入地圖中...'}
                </h3>
                <p className="text-sm text-gray-600">
                  {filteredGeoJsonData ? '此樓層沒有包含地理特徵' : '正在處理 IMDF 檔案...'}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* 右側面板 - 屬性編輯 */}
        <div className="w-80 bg-white shadow-sm border-l flex flex-col">
          <div className="p-4 border-b">
            <h2 className="font-semibold text-gray-900">
              {isEditing ? '新特徵屬性' : '屬性編輯'}
            </h2>
          </div>
          <div className="flex-1 overflow-y-auto">
            {displayFeature ? (
              <div className="p-4">
                <div className="mb-4">
                  <h3 className="font-medium text-gray-900 mb-1">
                    {isEditing ? '設定新特徵' : getIMDFDisplayName(displayFeature)}
                  </h3>
                  <p className="text-sm text-gray-500">
                    {displayFeature.properties?._file_type || selectedFeatureType}
                  </p>
                  {displayFeature.properties?.level_id && (
                    <p className="text-xs text-purple-600 mt-1">
                      樓層: {floorLevels.find(f => f.id === displayFeature.properties?.level_id)?.name || displayFeature.properties.level_id}
                    </p>
                  )}
                </div>
                
                {/* 屬性編輯區域 */}
                <div className="space-y-3">
                  {Object.entries(displayFeature.properties || {})
                    .filter(([key]) => !key.startsWith('_'))
                    .map(([key, value]) => (
                    <div key={key} className="space-y-1">
                      <label className="block text-sm font-medium text-gray-700">
                        {key}
                      </label>
                      <input
                        type="text"
                        value={String(value || '')}
                        onChange={(e) => handlePropertyEdit(key, e.target.value)}
                        onFocus={() => setEditingProperty(key)}
                        onBlur={() => setEditingProperty(null)}
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                          editingProperty === key ? 'border-blue-500' : 'border-gray-300'
                        }`}
                      />
                    </div>
                  ))}
                </div>

                {/* 操作按鈕 */}
                <div className="mt-6 space-y-3">
                  {isEditing ? (
                    // 新特徵的確認和取消按鈕
                    <>
                      <button
                        onClick={handleConfirmFeature}
                        className="w-full px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                      >
                        確認添加特徵
                      </button>
                      <button
                        onClick={handleCancelFeature}
                        className="w-full px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
                      >
                        取消
                      </button>
                      <button
                        onClick={handleAddProperty}
                        className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                      >
                        新增屬性
                      </button>
                    </>
                  ) : (
                    // 現有特徵的操作按鈕
                    <>
                      <button
                        onClick={handleAddProperty}
                        className="w-full px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                      >
                        新增屬性
                      </button>
                      
                      <button
                        onClick={handleFocusFeature}
                        className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                      >
                        定位到特徵
                      </button>
                      
                      <button
                        onClick={handleDeleteFeature}
                        className="w-full px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                      >
                        刪除特徵
                      </button>
                    </>
                  )}
                </div>
              </div>
            ) : (
              <div className="p-4 text-center text-gray-500">
                <div className="text-4xl text-gray-300 mb-4">
                  {mapMode === 'view' ? '👆' : mapMode === 'edit' ? '✏️' : '🎨'}
                </div>
                <h3 className="font-medium text-gray-900 mb-2">
                  {mapMode === 'view' ? '選取特徵進行編輯' : 
                   mapMode === 'edit' ? '選取特徵進行形狀編輯' : 
                   '繪製特徵'}
                </h3>
                <p className="text-sm">
                  {mapMode === 'view' ? '點擊地圖上的任何特徵來查看和編輯其屬性' : 
                   mapMode === 'edit' ? '選取特徵後可以拖拽節點來修改形狀' : 
                   '選擇特徵類型後，使用地圖工具繪製新特徵'}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default IMDFEditorWithFloors;